import { useEffect, useMemo, useState } from "react";
import { ManufacturingEntityBulkImportModalFrame } from "./ManufacturingEntityBulkImportModalFrame";
import { EXAMPLE_SLOT_LABELS_BULK_IMPORT_CSV } from "@/lib/manufacturing/slotLabelsBulkImportExample";
import { buildSlotLabelsBulkImportPreview } from "@/lib/manufacturing/slotLabelsBulkImportPreview";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import {
  bulkApplyManufacturingSlotLabels,
  listManufacturingSlotCleanupLines,
  type SlotCleanupMaterialLine,
} from "@/lib/manufacturing/slotLabelCleanupClient";

export function ManufacturingSlotLabelsBulkImportModal({
  enabled,
  onClose,
  onImported,
}: {
  enabled: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<SlotCleanupMaterialLine[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogBusy, setCatalogBusy] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setCatalogBusy(true);
      setCatalogError(null);
      const r = await listManufacturingSlotCleanupLines("all");
      if (cancelled) return;
      setCatalogBusy(false);
      if (!r.ok) {
        setCatalogError(r.error);
        setCatalog([]);
        return;
      }
      setCatalog(r.data.lines);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const preview = useMemo(
    () => buildSlotLabelsBulkImportPreview(text, catalog),
    [text, catalog],
  );

  const importableCount =
    preview.kind === "ok" ? preview.totals.applyable + preview.totals.unchanged : 0;

  async function handleSubmit() {
    if (preview.kind !== "ok") return;
    const rows = preview.rows
      .filter((r) => !r.blocked)
      .map((r) => ({
        line_id: r.resolved_line_id ?? r.line_id,
        recipe_code: r.recipe_code,
        material_code: r.material_code,
        material_name: r.material_name,
        line_quantity: r.line_quantity,
        slot_label: r.slot_label,
      }));
    if (rows.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    const result = await bulkApplyManufacturingSlotLabels({ rows });
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || "Bulk import failed");
      return;
    }
    onImported();
    onClose();
  }

  return (
    <ManufacturingEntityBulkImportModalFrame
      title="Bulk import slot label assignments (CSV / JSON)"
      formatGuide={
        <>
          <p>
            One row per BOM material line. Columns: <code>line_id</code> (optional),
            <code>recipe_code</code>, <code>material_code</code>, <code>material_name</code>,{" "}
            <code>line_quantity</code>, <code>slot_label</code>.
          </p>
          <p>
            When <code>line_id</code> is omitted, rows match by recipe + material code; include{" "}
            <code>line_quantity</code> if the recipe has duplicate materials.
          </p>
          {catalogBusy ? (
            <p className="text-amber-800 dark:text-amber-200">Loading line catalog for preview…</p>
          ) : catalogError ? (
            <p className="text-red-700 dark:text-red-300">Catalog load failed: {catalogError}</p>
          ) : (
            <p>{catalog.length.toLocaleString()} BOM lines available for matching.</p>
          )}
        </>
      }
      exampleCsv={EXAMPLE_SLOT_LABELS_BULK_IMPORT_CSV}
      onDownloadExample={() =>
        downloadCsv("mfg-slot-labels-example.csv", EXAMPLE_SLOT_LABELS_BULK_IMPORT_CSV)
      }
      text={text}
      onTextChange={setText}
      onFile={async (file) => setText(await file.text())}
      previewSummary={
        preview.kind === "empty" ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">Paste or upload a file to preview.</p>
        ) : preview.kind === "error" ? (
          <p className="text-sm text-red-700 dark:text-red-300">{preview.error}</p>
        ) : (
          <ul className="space-y-1 text-sm text-gray-800 dark:text-zinc-200">
            <li>
              Apply {preview.totals.applyable} · Unchanged {preview.totals.unchanged} · Blocked{" "}
              {preview.totals.blocked}
            </li>
            {preview.rows
              .filter((r) => r.blocked)
              .slice(0, 5)
              .map((r, i) => (
                <li key={`${r.recipe_code}-${r.material_code}-${i}`} className="text-xs text-red-700 dark:text-red-300">
                  {r.recipe_code || r.line_id || `row ${i + 1}`}: {r.blockReason}
                </li>
              ))}
          </ul>
        )
      }
      submitting={submitting || catalogBusy}
      submitError={submitError}
      importableCount={importableCount}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
      submitLabel="Apply labels"
    />
  );
}
